import { useState, useCallback, useEffect } from 'react';
import {FormDefinition, forms, FormState, ValidationResult} from "./forms";
import isEqual from "lodash/isEqual";

export interface FieldValueChangeArgs {
  fieldName: string,
  value: any,
  touched?: boolean
}

export type FormHook = {
  formState: FormState,
  formDef: FormDefinition,
  setFieldValue: (fieldName: string | FieldValueChangeArgs[], value?: any, touched?: boolean) => void,
  validate: () => ValidationResult,
  values: any,
};

const _getValues = (formState: FormState) => {
  const res: any = {};
  Object.keys(formState).forEach(key => {
    res[key] = formState[key].value;
  });
  return res;
};

/* useForm: hook de uso fácil de forms
Dicas:
1: para onChange de fields que provocam mudança do formDef, não usar onChange no FormField.
    - Colocar o formDef em um useState.
    - Obter o valor do campo no próximo render via getValues().
    - usar um useEffect para atualizar o state do formDef.
* */
export const useForm = (formDef: FormDefinition, initialValues?: any): FormHook => {
  const [_formDef, setFormDef] = useState(formDef);
  const [formState, setFormState] = useState<FormState>(forms.buildFormState(formDef, initialValues));
  const [values, setValues] = useState(initialValues || {});

  // set values
  useEffect(() => {
    const newValues = _getValues(formState);
    if (!isEqual(newValues, values)) {
      // console.log('== USEFORM set values ==', newValues);
      setValues(newValues);
    }
  }, [formState, values]);

  // set formDef
  useEffect(() => {
    if (!isEqual(formDef, _formDef)) {
      // console.log('== USEFORM reset DEF ==', {formDef, values});
      setFormDef(formDef);
      setFormState(forms.buildFormState(formDef, values));
    }
  }, [_formDef, formDef, values]);

  const setFieldValue = useCallback((fieldName: string | FieldValueChangeArgs[], value?: any, touched?: boolean): void => {
    let changes: FieldValueChangeArgs[];
    if (typeof fieldName === 'string') {
      changes = [{fieldName, value: value, touched: touched}];
    } else {
      changes = fieldName;
    }
    if (!changes || changes.length === 0)
      return;

    // console.log('== SETFIELDVALUE PRE ==', {fieldName, changes});

    setFormState(_state => {
      const newState = {..._state};
      changes.forEach(({fieldName, value, touched}) => {
        if (!_formDef[fieldName]) {
          // console.log(`setFieldValue skiping ${fieldName}`);
          return;
        }

        if (touched === undefined)
          touched = true;

        newState[fieldName] = forms.validateField(_formDef[fieldName], _state[fieldName], value, touched);
      });

      if (isEqual(newState, _state)) {
        // console.log('== SETFIELDVALUE SKIP ==', {fieldName, changes, _state});
        return _state;
      }

      // console.log('== SETFIELDVALUE ==', {fieldName, changes, newState});
      setValues(_getValues(newState));
      return newState;
    });
  }, [_formDef]);

  /* validate
  Nao usar em um effect, o formState é alterado a cada chamada
  Nao usar na raiz do componente, o formState é alterado a cada chamada
  * */
  const validate = useCallback((): ValidationResult => {
    const {result, state} = forms.validate(_formDef, formState);
    setFormState(state);
    return result;
  }, [_formDef, formState]);


  return {
    formState,
    formDef: _formDef,
    setFieldValue,
    validate,
    // isValid,
    // getValues,
    values,
  };
}